/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import {
    EnduranceRBACGroupsApiService,
    EnduranceRBACResourcesApiService,
    EnduranceRBACRolesApiService,
    EnduranceRBACUsersApiService
} from '../../../../shared';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LoadingService, LogService, NavigationService } from '../../../../../../core/services';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-endurance-add-edit-roles',
    templateUrl: './endurance-add-edit-roles.component.html'
})

export class EnduranceAddEditRolesComponent extends BaseComponent {
    /**
     * Api data for RBAC groups
     */
    public data: any;

    /**
     * Grouping of endpoints
     */
    public grouping: any = [
        { service: 'base', friendlyName: 'Base' },
        { service: 'rbac', friendlyName: 'RBAC' },
        { service: 'gslb', friendlyName: 'GSLB' },
        { service: 'cluster', friendlyName: 'Cluster' },
        { service: 'certs', friendlyName: 'Certificates' },
        { service: 'ciphers', friendlyName: 'Ciphers' },
        { service: 'haproxy', friendlyName: 'HAProxy' },
        { service: 'fallbackServer', friendlyName: 'Fallback Server' },
        { service: 'pulseha', friendlyName: 'Pulse HA' },
        { service: 'ipGroups', friendlyName: 'IP Groups' },
        { service: 'sips', friendlyName: 'Static IPs' },
        { service: 'fip', friendlyName: 'Floating IPs' },
        { service: 'regions', friendlyName: 'Regions' },
        { service: 'serviceIPs', friendlyName: 'Service IPs' },
        { service: 'healthchecks', friendlyName: 'Healthchecks' },
        { service: 'snapshotsDB', friendlyName: 'Database Snapshots' },
        { service: 'system', friendlyName: 'System' },
        { service: 'dataServices', friendlyName: 'Data Services' },
        { service: 'licence', friendlyName: 'Licence' },
        { service: 'support', friendlyName: 'Support' },
        { service: 'logs', friendlyName: 'Event Logs' },
        { service: 'update', friendlyName: 'Updates' },
        { service: 'interfaces', friendlyName: 'Interfaces' },
        { service: 'snapshotLVM', friendlyName: 'LVM Snapshot' },
        { service: 'sidecar', friendlyName: 'Shuttle config' },
        { service: 'backup', friendlyName: 'Backups' },
        { service: 'snmp', friendlyName: 'SNMP' },
        { service: 'config', friendlyName: 'View Config' },
    ];

    constructor(
        private activatedRoute: ActivatedRoute,
        private loadingService: LoadingService,
        private logService: LogService,
        private enduranceRBACUsersApiService: EnduranceRBACUsersApiService,
        private enduranceRBACRolesApiService: EnduranceRBACRolesApiService,
        private enduranceRBACGroupsApiService: EnduranceRBACGroupsApiService,
        private enduranceRBACResourcesApiService: EnduranceRBACResourcesApiService,
        private navigateService: NavigationService,
        private router: Router,
    ) {
        super();

        const id: string = this.activatedRoute.snapshot.paramMap.get('id');
        const duplicate: boolean = !!this.activatedRoute.snapshot.paramMap.get('duplicate');
        const editId: string = id ? id : null;
        this.setUpPageData(editId, duplicate);
    }

    /**
     * Submit.
     * @param roleValues
     */
    public submit(roleValues: any): void {
        const role: any = {
            id: roleValues['id'],
            name: roleValues['name'],
            users: roleValues['users'],
            groups: roleValues['groups'],
            endpoints: this.cleanEndpoints(roleValues['endpoints'])
        };

        const apiCall = role.id
            ? this.enduranceRBACRolesApiService.editRole(role)
            : this.enduranceRBACRolesApiService.createRole(role);

        apiCall
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    const url: string = this.navigateService.getEnduranceUrl('/maintenance/users/list');
                    this.router.navigate([url]).then().catch();
                },
                error: (error): void => {
                    this.logService.error('PortalAddEditRolesComponent: submit', error.message);
                    this.loadingService.loadingButtonEmitter.next(false);
                }
            });
    }

    /**
     * Clean endpoints - only submit endpoints with values that are true.
     * @param endpoints
     * @private
     */
    private cleanEndpoints(endpoints: any[]): any[] {
        const cleanEndpoints: any[] = [];
        for (const endpoint of endpoints) {
            const methods = endpoint.methods;
            if (Object.values(methods).some((item): boolean => item === true)) {
                const newEndpoint: any = {
                    path: endpoint.path,
                    service: endpoint.service,
                    methods: this.getSetMethods(methods)
                };

                cleanEndpoints.push(newEndpoint);
            }
        }

        return cleanEndpoints;
    }

    /**
     * Get methods that have true set.
     * @param methods
     * @private
     */
    private getSetMethods(methods: any): any[] {
        const newMethod: any[] = [];
        for (const method in methods) {
            if (methods.hasOwnProperty(method)) {
                if (methods[method] === true) {
                    newMethod.push(method);
                }
            }
        }

        return newMethod;
    }

    /**
     * Set up page data for edit or duplicate.
     * @param id
     * @param duplicate
     * @private
     */
    private setUpPageData(id: string, duplicate: boolean): void {
        const apiCalls: any = {
            users: this.enduranceRBACUsersApiService.getAllUsers(),
            roles: this.enduranceRBACRolesApiService.getAllRoles(),
            groups: this.enduranceRBACGroupsApiService.getAllGroups(),
            endpoints: this.enduranceRBACResourcesApiService.getAllRoutes()
        };

        if (id) {
            apiCalls['rolePatch'] = this.enduranceRBACRolesApiService.getRoleById(id);
        }

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    dataGroups['endpoints'] = this.formatEndpoints(dataGroups['endpoints']);
                    dataGroups['isDuplicate'] = duplicate;
                    if (id) {
                        dataGroups['rolePatch']['endpoints'] = this.formatUserEndpoints(dataGroups['endpoints'], dataGroups);
                    }
                    this.data = dataGroups;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('PortalAddEditRolesComponent: setUpPageData', error.message);
                }
            });
    }

    /**
     * Format backend data to be the same as current users endpoints.
     * @param endpoints
     * @private
     */
    private formatEndpoints(endpoints: any[]): any[] {
        const newEndpoints: any[] = [];
        for (const endpoint of endpoints) {
            const newEndpoint: any = {
                path: endpoint.path,
                service: endpoint.service,
                methods: endpoint.methods,
                friendlyName: this.getFriendlyName(endpoint.path)
            };

            newEndpoints.push(newEndpoint);
        }

        return newEndpoints;
    }

    /**
     * Format user endpoints.
     * @param endpoints
     * @param dataGroups
     * @private
     */
    private formatUserEndpoints(endpoints: any[], dataGroups: any): any[] {
        const userEndpoints = dataGroups.rolePatch.endpoints;
        const newEndpoints: any[] = [];
        for (const endpoint of endpoints) {
            const newEndpoint: object = {
                path: endpoint.path,
                service: endpoint.service,
                methods: userEndpoints.find((item: { path: any; }): boolean => item.path === endpoint.path)
                    ? this.getNewMethod(userEndpoints, endpoint)
                    : endpoint.methods,
                friendlyName: this.getFriendlyName(endpoint.path)
            };

            newEndpoints.push(newEndpoint);
        }

        return newEndpoints;
    }

    /**
     * Get new Method.
     * @param userEndpoints
     * @param endpoint
     * @private
     */
    private getNewMethod(userEndpoints: any[], endpoint: { methods: any; path: any; }): any {
        const methods = endpoint.methods;
        const newMethod: object = {};
        const userMethods = userEndpoints.find((item: any): boolean => item.path === endpoint.path).methods;
        const cleanMethods = userMethods.filter((item: string): boolean => item !== '');

        for (const method in methods) {
            if (methods.hasOwnProperty(method)) {
                newMethod[method] = cleanMethods.findIndex((item: string): boolean => item === method) !== -1;
            }
        }

        return newMethod;
    }

    /**
     * Get friendly names.
     * @param path
     * @private
     */
    private getFriendlyName(path: string): string {
        const friendlyName = {
            '/routes': 'List all endpoints',
            '/routes/:userID': 'List endpoints available to a specific user',
            '/system/console': 'Access SSH and make changes',
            '/system/reboot': 'Reboot the system',
            '/system/halt': 'Shutdown the appliance',
            '/system/execute': 'Run shell commands',
            '/locks': 'Get locks that are in place',
            '/locks/app': 'Unlock the appliance after system fail',
            '/locks/user': 'Lock the appliance',
            '/services': 'List services',
            '/services/apply': 'Restart/reload all pending services',
            '/services/:service/restart': 'Restart a specific service',
            '/services/:service/reload': 'Reload a specific service',
            '/stats/config/:service': 'View config by service',
            //     RBAC
            '/rbac/groups': 'Create or list groups',
            '/rbac/groups/:id': 'Update or delete a group',
            '/rbac/users': 'Create or list users',
            '/rbac/users/:id': 'Update or delete a user',
            '/rbac/current': 'Allow current user to update account settings',
            '/rbac/roles': 'Create or list roles',
            '/rbac/roles/:id': 'Update or delete a user',
            //     GSLB
            '/gslb/globalnames': 'Create or list global names',
            '/gslb/globalnames/:id': 'Update or delete a global name',
            '/gslb/members': 'Create or list members',
            '/gslb/members/:id': 'Update or delete a member',
            '/gslb/config': 'Get GSLB config',
            '/gslb/config/download': 'Get GSLB config and download to file',
            '/gslb/config/upload': 'Upload a GSLB config',
            '/gslb/config/restore': 'Restore a GSLB config',
            '/gslb/stats/generic-state': 'Get generic status',
            '/gslb/stats/ppdns-state': 'Get PPDNS status',
            '/gslb/stats/get-heartbeat': 'Get getHeartbeat status',
            '/gslb/pools': 'Create or list pools',
            '/gslb/pools/:id': 'Update or delete a pool',
            '/gslb/pools/:id/members': 'List or update pool members',
            '/gslb/pools/:id/members/:memberID': 'Delete a pool member',
            '/gslb/topologies': 'Create or list topologies',
            '/gslb/topologies/:id': 'Update or delete a topology',
            // Cluster
            '/cluster/join': 'Join a cluster',
            '/cluster/restore': 'Restore a cluster',
            '/cluster/promote/:id': 'Promote a node in the cluster',
            '/cluster/resync': 'Resync all nodes in a cluster',
            '/cluster/:id': 'Delete a node from a cluster',
            '/cluster/services-apply': 'Restart/reload all pending services across a cluster',
            // Certificates
            '/certificates/cas': 'Create or list certificates authorities',
            '/certificates/cas/:id': 'Update or delete certificates authority',
            '/certificates/certs': 'Create or list certificates',
            '/certificates/certs/:id': 'Update or delete certificates',
            '/certificates/crls': 'Create or list certificate revocation lists',
            '/certificates/crls/:id': 'Update or delete certificate revocation lists',
            '/certificates/csrs': 'Create or list certificates signing request(s)',
            '/certificates/csrs/:id': 'Update or delete certificate signing request',
            '/certificates/csrs/:id/renew': 'Renew a certificate',
            '/certificates/csrs/:id/apply': 'Apply a certificate',
            '/certificates/signature-algorithm': 'List of supported signature algorithms',
            // Ciphers
            '/ciphers': 'List ciphers',
            '/ciphers/lists': 'Create or list ciphers',
            '/ciphers/lists/:id': 'Update or delete cipher',
            // HA Proxy
            '/haproxy/acls': 'Create or list ACLs',
            '/haproxy/acls/:id': 'Update or delete an ACL',
            '/haproxy/acls/hanging': 'List hanging ACLs',
            '/haproxy/backends/:id/acls': 'List ACLs for a specific backend',
            '/haproxy/backends/:id': 'Update or delete a backend ACL',
            '/haproxy/backends': 'Create or list backends',
            '/haproxy/backends/:id/rips': 'List rips for a specific backend',
            '/haproxy/backends/:id/halt': 'Halt a backend',
            '/haproxy/backends/:id/drain': 'Drain a backend',
            '/haproxy/backends/:id/online': 'Online a backend',
            '/haproxy/frontends/:id/acls': 'List ACLs for a specific frontend',
            '/haproxy/frontends': 'Create or list frontends',
            '/haproxy/frontends/:id': 'Update or delete frontend',
            '/haproxy/frontends/:id/backends': 'List backends for a specific frontend',
            '/haproxy/frontends/:id/all': 'Delete a frontend and all associated services',
            '/haproxy/frontends/:id/halt': 'Halt a frontend',
            '/haproxy/frontends/:id/drain': 'Drain a frontend',
            '/haproxy/frontends/:id/online': 'Online a frontend',
            '/haproxy/frontends/:id/certs': 'Create or list certificates for a frontend',
            '/haproxy/frontends/:id/certs/:certId': 'Update or delete a frontend certificate',
            '/haproxy/global': 'Get HA.Proxy configuration',
            '/haproxy/headers/:id': 'Update or delete header',
            '/haproxy/headers': 'Create or list headers',
            '/haproxy/headers/hanging': 'List hanging headers',
            '/haproxy/rips': 'Create or list real servers',
            '/haproxy/rips/:id': 'Update or delete a real server',
            '/haproxy/rips/:id/halt': 'Halt a real server',
            '/haproxy/rips/:id/drain': 'Drain a real server',
            '/haproxy/rips/:id/online': 'Online a real server',
            '/haproxy/sticktable/:id/:start/:end': 'List stick table(s)',
            '/haproxy/sticktable/:id': 'Delete stick table by backend',
            '/haproxy/sticktable/:id/:type/:item': 'Delete stick table by type',
            '/haproxy/stats': 'Get HA statistics',
            '/haproxy/stats/frontends': 'List all frontends statistics',
            '/haproxy/stats/frontends/:id': 'List statistics for a specific frontend',
            '/haproxy/stats/backends': 'List all backends statistics',
            '/haproxy/stats/backends/:id': 'List statistics for a specific backend',
            '/haproxy/backends/hanging': 'List all hanging backends',
            '/haproxy/stats/rips': 'List all real servers statistics',
            '/haproxy/stats/rips/:id': 'List statistics for a specific real server',
            '/haproxy/stats/rips/backend/:id': 'List statistics for a specific real server\'s backend',
            '/haproxy/rips/hanging': 'List all hanging real servers',
            // Fallback
            '/fallback': 'Create or list fallback server(s)',
            '/fallback/:id': 'Update or delete a fallback server',
            // Pulse HA
            '/pulseha/globals': 'List or update Pulse HA global configuration',
            '/pulseha': 'Pulse HA configuration',
            // IP Groups
            '/ipgroups': 'Create or list ip group(s)',
            '/ipgroups/:id': 'Update or delete a ip group',
            '/sips': 'Create or list static ip(s)',
            '/sips/:id': 'Update or delete a ip group',
            '/fips': 'Create or list floating ip(s)',
            '/fips/:id': 'Update or delete a floating ip',
            '/fips/enable': 'Enable a floating ip',
            '/fips/disable': 'Enable a floating ip',
            '/service-ips': 'List of service ips',
            '/service-ips/:id': 'Update or delete a ip group',
            // Regions
            '/regions': 'Create or list a region(s)',
            '/regions/:id': 'Read, update or delete a specific region',
            // Healthchecks
            '/healthchecks': 'Create or list healthchecks(s)',
            '/healthchecks/:id': 'Update or delete a healthcheck',
            '/healthchecks/service/:service': 'Get healthchecks by service',
            // DB
            '/snapshots/db': 'Create or list database snapshot(s)',
            '/snapshots/db/:id': 'Update or delete a database snapshot',
            // System
            '/system/datetime': 'Set system datetime',
            '/system/datetime/timezones': 'List system timezones',
            '/system/datetime/timezone': 'Set system timezone',
            '/system/datetime/ntp': 'Set system network time protocol',
            '/system/keymaps': 'List system keymaps',
            '/system/keymap': 'Set system keymap',
            '/system/logging': 'Set system logging details',
            '/system/mail': 'Set system SMTP',
            '/system/proxy': 'Set system proxy',
            '/system/userinterface': 'Set system userinterface',
            '/system/hostname': 'Set system hostname',
            '/system/conntrack': 'Set system conntrack table size',
            '/system-stats/settings': 'Set system settings',
            '/system-stats': 'Set system statistics',
            // Data Services
            '/data-services': 'List data services',
            '/data-services/:name': 'Update data service',
            // License
            '/licence': 'Set licence',
            // Support
            '/support/status': 'Get system status for support',
            '/support/download/:option': 'Get system logs',
            '/support/download/file/:filename': 'Get system log file',
            // Event logs
            '/logs': 'List logs',
            '/logs/services': 'List available system services',
            // Updates
            '/system/update/status/force': 'Get system update force (avoid cache)',
            '/system/update': 'System updates',
            '/system/update/majorversion': 'Update major system version',
            // Interfaces
            '/interfaces': 'Create or list interfaces',
            '/interfaces/:id': 'Update or delete an interface',
            '/interfaces/:id/group': 'Update groups assigned to an interface',
            '/interfaces/:id/group/:groupId': 'Deletes a group from an interface',
            // Snapshots
            '/lvmsnapshots': 'Create, read, update or delete a disk snapshot',
            // Shuttle config
            '/portal/sidecar': 'List or update portal shuttle',
            '/portal/status': 'Get portal shuttle status',
            '/portal/adopt': 'Start shuttle adoption process',
            '/portal/remote': 'Link to a remote shuttle',
            // Backups
            '/backup': 'Backup system or delete backup',
            '/backup/upload': 'Upload a backup',
            '/backup/list': 'List available backups',
            '/backup/restore': 'Restore a backup from a file',
            '/backup/uploads': 'Delete a backup',
            '/backup/auto': 'Delete all automatic backups',
            '/backup/:filename': 'Deletes backup by filename',
            // SNMP
            '/snmp': 'List or update SNMP settings',
            '/snmp/users': 'Create SNMP users',
            '/snmp/users/:id': 'Update or delete an SNMP user',
            // CONFIG
            '/configs': 'List all config options',
            '/configs/:module/:config': 'Get config data'
        };

        return friendlyName[path] !== '' ? friendlyName[path] : null;
    }
}
